import React, { Fragment, useRef, useState } from 'react';
import { Box, Divider, Fade, Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactComponent as AddRowAboveIcon } from 'assets/images/TableToolbarIcons/add_row.svg';
import { ReactComponent as AddRowBelowIcon } from 'assets/images/TableToolbarIcons/add_row_below.svg';
import { ReactComponent as AddColumnBeforeIcon } from 'assets/images/TableToolbarIcons/add_column_before.svg';
import { ReactComponent as AddColumnAfterIcon } from 'assets/images/TableToolbarIcons/add_column_after.svg';
import { ReactComponent as AddSectionHeaderIcon } from 'assets/images/TableToolbarIcons/add_section_header.svg';
import { ReactComponent as RemoveRowIcon } from 'assets/images/TableToolbarIcons/remove_row.svg';
import { ReactComponent as RemoveColumnIcon } from 'assets/images/TableToolbarIcons/remove_column.svg';
import { ReactComponent as MergeCellsIcon } from 'assets/images/TableToolbarIcons/merge_cells.svg';
import { ReactComponent as UnmergeCellsIcon } from 'assets/images/TableToolbarIcons/unmerge_cells.svg';
import { ReactComponent as BackgroundColorIcon } from 'assets/images/TableToolbarIcons/background_color.svg';
import { ReactComponent as InsertImageIcon } from 'assets/images/TableToolbarIcons/insert_image.svg';
import styles from './TableToolbar.module.css';
import { MuiColorInput } from 'mui-color-input';

const DIVIDER_POSITIONS = [4, 6, 8, 9];

const DocGeniusTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#323232',
    borderRadius: '8px',
    color: '#F2F3F3',
    maxWidth: 220,
    fontSize: '12px',
    fontFamily: 'Rubik',
    fontWeight: 'normal',
    padding: '8px 16px',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#323232',
  },
}));

interface TableToolbarProps {
  disableAddColumns: boolean;
  disableAddRows: boolean;
  disableMergeCells: boolean;
  disableUnmergeCells: boolean;
  disableRemoveColumns: boolean;
  disableRemoveRows: boolean;
  hideTooltips: boolean;
  handleApplyBackgroundColor: (color: string) => void;
  handleAddRowAbove: () => void;
  handleAddRowBelow: () => void;
  handleAddColumnBefore: () => void;
  handleAddColumnAfter: () => void;
  handleAddHeader: () => void;
  handleMergeCells: () => void;
  handleUnmergeCells: () => void;
  handleRemoveRow: () => void;
  handleRemoveColumn: () => void;
}

const TableToolbar = ({
  disableAddColumns,
  disableAddRows,
  disableMergeCells,
  disableUnmergeCells,
  disableRemoveColumns,
  disableRemoveRows,
  hideTooltips,
  handleApplyBackgroundColor,
  handleAddColumnAfter,
  handleAddColumnBefore,
  handleAddRowAbove,
  handleAddRowBelow,
  handleAddHeader,
  handleMergeCells,
  handleUnmergeCells,
  handleRemoveColumn,
  handleRemoveRow,
}: TableToolbarProps) => {
  const [selectedColor, setSelectedColor] = useState('#FFFF');
  const colorPickerRef = useRef<HTMLDivElement>(null);

  const actionButtons = [
    {
      title: 'Add row above',
      Icon: AddRowAboveIcon,
      onClick: handleAddRowAbove,
      isDisabled: disableAddRows,
    },
    {
      title: 'Add row below',
      Icon: AddRowBelowIcon,
      isDisabled: disableAddRows,
      onClick: handleAddRowBelow,
    },
    {
      title: 'Add column before',
      Icon: AddColumnBeforeIcon,
      isDisabled: disableAddColumns,
      onClick: handleAddColumnBefore,
    },
    {
      title: 'Add column after',
      Icon: AddColumnAfterIcon,
      isDisabled: disableAddColumns,
      onClick: handleAddColumnAfter,
    },
    {
      title: 'Add section header',
      Icon: AddSectionHeaderIcon,
      isDisabled: disableAddRows,
      onClick: handleAddHeader,
    },
    {
      title: 'Remove row',
      Icon: RemoveRowIcon,
      isDisabled: disableRemoveRows,
      onClick: handleRemoveRow,
    },
    {
      title: 'Remove column',
      Icon: RemoveColumnIcon,
      isDisabled: disableRemoveColumns,
      onClick: handleRemoveColumn,
    },
    {
      title: 'Merge cells',
      Icon: MergeCellsIcon,
      isDisabled: disableMergeCells,
      onClick: handleMergeCells,
    },
    {
      title: 'Unmerge cells',
      Icon: UnmergeCellsIcon,
      isDisabled: disableUnmergeCells,
      onClick: handleUnmergeCells,
    },
    {
      title: 'Background color',
      Icon: BackgroundColorIcon,
    },
    {
      title: 'Insert image',
      Icon: InsertImageIcon,
      isDisabled: true,
    },
  ];

  const openColorPicker = () => {
    if (colorPickerRef.current) {
      const buttonInsideColorPicker = colorPickerRef.current.querySelector('button');
      if (buttonInsideColorPicker) {
        buttonInsideColorPicker.click();
      }
    }
  };

  const handleBackgroundColorButtonClick = () => {
    openColorPicker();
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);

    if (handleApplyBackgroundColor) {
      handleApplyBackgroundColor(color);
    }
  };

  return (
    <Box
      alignItems='center'
      className={styles.tableToolbar}
      display={hideTooltips ? 'none' : ' flex'}
      justifyContent='center'
    >
      {actionButtons.map(({ title, isDisabled, Icon, onClick }, index) => {
        const isBackgroundColorButton = Icon === BackgroundColorIcon;
        return (
          <Fragment key={index}>
            <DocGeniusTooltip
              arrow
              title={title}
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 600 }}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -5],
                      },
                    },
                  ],
                },
              }}
            >
              <span>
                <button
                  disabled={isDisabled}
                  className={`${isDisabled ? styles.disabledToolbarActionable : styles.toolbarActionableContainer} ${index === actionButtons.length - 1 && styles.lastToolbarItem} ${index === 0 && styles.firstToolbarItem}`}
                  onClick={isBackgroundColorButton ? handleBackgroundColorButtonClick : onClick}
                >
                  {Icon && <Icon className={styles.icon} />}
                </button>
              </span>
            </DocGeniusTooltip>
            {DIVIDER_POSITIONS.includes(index) && (
              <Divider color='#B8C1CC' orientation='vertical' variant='middle' flexItem />
            )}
          </Fragment>
        );
      })}
      <div className={styles.toolbarColorPicker}>
        <MuiColorInput value={selectedColor} onChange={handleColorChange} ref={colorPickerRef} />
      </div>
    </Box>
  );
};

export default TableToolbar;
